import * as React from "react";

import type { AppContextType } from "@1js/acui-context";
import { AppContext } from "@1js/acui-context";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type {
  IContextualMenuItem,
  IContextualMenuProps,
  IFocusZone,
  IPopupRestoreFocusParams,
  ICalloutPositionedInfo,
} from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { ContextualMenu, getRTL, KeyCodes } from "@fluentui/react";

import { UILocation } from "@1js/acui-common";
import { platformFeatures } from "@1js/acui-experiments";
import { withMeasuredContext } from "@1js/acui-resize-group";
import { withShadowRendering } from "@1js/acui-shadow-rendering";
import { findParentElementByCondition, scrollBarWidth } from "@1js/acui-common";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import type { AppContextualMenuProps } from "./types";

interface AppContextualMenuState {
  focusInRightClickMenu?: boolean;
  alwaysRestoreFocus?: boolean;
}

/**
 * This component is the customized version of the Fabric contextual menu.
 * Currently, it does not do much customization, but we will add more logic as our
 * shadow rendering gets more sophisticated.
 */
export class AppContextualMenuBase extends React.PureComponent<
  AppContextualMenuProps,
  AppContextualMenuState
> {
  static contextType: React.Context<AppContextType> = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  private focusZoneRef = React.createRef<IFocusZone>();
  private focusZoneElement = React.createRef<HTMLDivElement>();
  private scrollBarOffsetApplied: boolean = false;
  private shouldRestoreFocus: boolean = false;

  constructor(props: AppContextualMenuProps) {
    super(props);

    this.state = {
      focusInRightClickMenu: false,
      alwaysRestoreFocus: props.alwaysRestoreFocus,
    };
  }

  public render() {
    const { isMeasured, focusZoneProps, calloutProps, items, id } = this.props;

    if (isMeasured) {
      return null;
    }

    const supportsRightClickMenu = this.supportsRightClickMenu();

    // when cleaning up the feature gate below, change let to const.
    let shouldFocusOnMount = supportsRightClickMenu
      ? !this.state.focusInRightClickMenu
      : this.props.shouldFocusOnMount;
    if (
      !this.context.isExperimentalFeatureEnabled?.(
        "Microsoft.Office.SharedOnline.AutoOpenSubmenuFixes"
      )
    ) {
      shouldFocusOnMount = supportsRightClickMenu
        ? !this.state.focusInRightClickMenu
        : undefined;
    }

    const delayUpdateFocusOnHover: boolean | undefined =
      this.props.delayUpdateFocusOnHover ??
      (supportsRightClickMenu ? this.state.focusInRightClickMenu : undefined);

    if (items?.findIndex((item) => item === undefined) >= 0) {
      this.context.logger?.onMessage(
        `AppContextualMenu with id ${id} attempting to render an undefined item`
      );
    }

    return (
      <>
        <ContextualMenu
          {...this.props}
          onMenuOpened={this.onMenuOpened}
          focusZoneProps={{
            ...focusZoneProps,
            componentRef: this.focusZoneRef,
            elementRef: this.focusZoneElement,
          }}
          onRestoreFocus={this.restoreFocus}
          onDismiss={this.onDismiss}
          delayUpdateFocusOnHover={delayUpdateFocusOnHover}
          shouldFocusOnMount={shouldFocusOnMount}
          calloutProps={{
            preventDismissOnEvent: supportsRightClickMenu
              ? this.preventDismissOnEvent
              : undefined,
            ...calloutProps,
            onPositioned: this.onPositioned,
            layerProps: {
              ...calloutProps?.layerProps,
              eventBubblingEnabled:
                calloutProps?.layerProps?.eventBubblingEnabled ??
                supportsRightClickMenu,
            },
          }}
        />
      </>
    );
  }

  private restoreFocus = (params: IPopupRestoreFocusParams) => {
    /**
     * In most cases in the function, we don't actually want to do anything and we want
     * to allow focus to move around as it normally does. This ensures that modal dialogs
     * opened via a menu command properly get focus. However, when we use escape to dismiss
     * the menu, we want to ensure focus goes to the original element aka the element hosting
     * the menu. This makes it easy to keyboard users to open and close menus and keep focus
     * on the same element.
     */

    // Check if we should be restoring focus, if the menu has focus (containsFocus) and we haven't
    // clicked outside, and if the originalElement is valid before moving focus.
    const { target } = this.props;
    if (
      (this.shouldRestoreFocus || this.state.alwaysRestoreFocus) &&
      params.containsFocus &&
      target
    ) {
      /**
       * In v8 button code in fluent we set the target element to an element so we can cast as an HTMLElement and call focus.
       * However for v9 button, we set the target element to a ref<htmlelement>, so we call focus on the original element.
       * For v9 buttons, the object has a property current with a value of null but for v8, current doesn't exist.
       */
      const el = target as HTMLElement;
      if (
        typeof target === "object" &&
        !target.hasOwnProperty("current") &&
        el &&
        el.focus
      ) {
        // Task: 6187837 cleanup checking for v8 buttons on restoring focus to menu item, once we're fully moved to v9 buttons
        // target is a v8 button
        el?.focus();
      } else {
        // target is a v9 button
        params.originalElement?.focus();
      }
      this.shouldRestoreFocus = false;
    }
  };

  private onDismiss = (
    ev?: React.MouseEvent | React.KeyboardEvent,
    dismissAll?: boolean
  ) => {
    const { onDismiss } = this.props;

    // Check if we are being dismissed via a dismiss menu key event and set the
    // bool to restore focus.
    const keyboardEvent: React.KeyboardEvent<HTMLElement> =
      ev as React.KeyboardEvent<HTMLElement>;
    if (
      keyboardEvent &&
      (keyboardEvent.keyCode === KeyCodes.escape ||
        (keyboardEvent.altKey && keyboardEvent.keyCode === KeyCodes.up) ||
        keyboardEvent.keyCode === KeyCodes.alt)
    ) {
      this.shouldRestoreFocus = true;
    }

    if (onDismiss) {
      onDismiss(ev, dismissAll);
    }

    if (this.state.focusInRightClickMenu) {
      this.setState({ focusInRightClickMenu: false });
    }
    this.scrollBarOffsetApplied = false;

    if (
      this.context.isPlatformFeatureEnabled?.(
        platformFeatures.modalContextMenuIsEnabled
      )
    ) {
      const menuDismissElement: HTMLElement | null = document.querySelector(
        ".menu-Dismiss-element"
      );

      if (!!menuDismissElement) {
        document.body.removeChild(menuDismissElement);
      }
    }
  };

  /**
   * Ensure that tab will move focus to the first menuitem button,
   * if focus is initially on the container.
   */
  private onMenuOpened = (contextualMenu?: IContextualMenuProps) => {
    if (this.props.onMenuOpened) {
      this.props.onMenuOpened(contextualMenu);
    }

    // Reset focus on first menuitem
    if (
      !this.props.shouldFocusOnContainer &&
      this.focusZoneRef &&
      this.focusZoneRef.current
    ) {
      this.focusZoneRef.current.focusElement(this.getFirstMenuItem());
    }
  };

  private applyContextualMenuSlideFix = () => {
    /*
   Check if any v9 Preview Gallery experiments are enabled and apply the context menu slide fix.
   If not, check if the slide fix experiment is enabled and apply the fix for already running v8 code
    */
    return (
      this.props?.directionalHint === 6 &&
      (checkExperiment(
        this.context,
        ribbonExperiments.fluentV9TableStylesPreviewGalleryIsEnabled
      ) ||
        checkExperiment(
          this.context,
          ribbonExperiments.fluentV9RibbonFontStyleGalleryIsEnabled
        ) ||
        checkExperiment(
          this.context,
          ribbonExperiments.fluentV9RibbonIconPreviewGalleryIsEnabled
        ) ||
        checkExperiment(
          this.context,
          ribbonExperiments.isV8AppContextMenuSlideFixEnabled
        ))
    );
  };

  private onPositioned = (positions?: ICalloutPositionedInfo) => {
    // When menus have a scrollbar, create additional space so it doesn't occlude the menu content
    if (
      !this.scrollBarOffsetApplied &&
      findParentElementByCondition(
        this.focusZoneElement.current,
        (el: HTMLElement) =>
          el.scrollHeight > el.clientHeight && el.clientHeight > 0
      )
    ) {
      let directionToUse: "left" | "right" = getRTL() ? "left" : "right";
      // We are checking for the condition, when we are getting the slide, and applying the fix only for those cases.
      if (
        this.applyContextualMenuSlideFix() &&
        positions?.elementPosition["right"] &&
        positions?.elementPosition["left"]
      ) {
        directionToUse = getRTL() ? "right" : "left";
      }
      let currentPositionValue = positions?.elementPosition[directionToUse];
      const calloutContainer = findParentElementByCondition(
        this.focusZoneElement.current,
        (el: HTMLElement) =>
          parseInt(el.style?.[directionToUse]?.split("px")[0], 10) ===
          Math.trunc(currentPositionValue || 0)
      );

      if (calloutContainer && currentPositionValue) {
        if (currentPositionValue < scrollBarWidth) {
          // There isn't enough room to shift that direction, instead shift the other way.
          directionToUse = directionToUse === "left" ? "right" : "left";
          currentPositionValue = positions?.elementPosition[directionToUse];
        }

        calloutContainer.style[directionToUse] = `${
          currentPositionValue! - scrollBarWidth
        }px`;
        this.scrollBarOffsetApplied = true;
      }
    }
    this.props.calloutProps?.onPositioned?.(positions);

    const subMenuToExpand = this.getContextualMenuItemToExpand(this.props.items)
      ?.componentRef as any;

    if (subMenuToExpand) {
      subMenuToExpand.current?.openSubMenu();
    }

    if (
      this.context.isPlatformFeatureEnabled?.(
        platformFeatures.modalContextMenuIsEnabled
      )
    ) {
      // TODO: TRACKING Fluent UI issue for Mac: Allow apps that use BaseButton's menus, to configure a property that makes the context menu modal.
      // https://office.visualstudio.com/OC/_workitems/edit/8546968
      // Mac: When the context menu is up, clicking on the surface around the context
      // menu should only dismiss the context menu and not perform any other action.
      const menuDismissElement = document.createElement("div");
      menuDismissElement.className = "menu-Dismiss-element";
      menuDismissElement.style.width = "100vw";
      menuDismissElement.style.height = "100vh";
      menuDismissElement.style.zIndex = "10000";
      menuDismissElement.style.position = "fixed";
      menuDismissElement.style.inset = "0px";

      menuDismissElement.addEventListener("click", (e) => {
        if (e?.target === menuDismissElement) {
          this.onDismiss();
        }
      });

      document.body.appendChild(menuDismissElement);
    }
  };

  private getFirstMenuItem = (): HTMLElement | undefined => {
    const menuId =
      this.props.id ?? (this.props.calloutProps && this.props.calloutProps.id);
    if (menuId) {
      const menuElement = document.getElementById(menuId);
      if (menuElement) {
        const firstMenuItem = menuElement.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstMenuItem) {
          return firstMenuItem as HTMLElement;
        }
      }
    }

    return undefined;
  };

  /**
   * Prevent dismissing the contextual menu when focusing onto a context menu when the contextual menu supports context menus
   */
  private preventDismissOnEvent = (
    ev: Event | React.FocusEvent | React.KeyboardEvent | React.MouseEvent
  ) => {
    const focusEvent = ev as React.FocusEvent;
    // Don't check if we should prevent dismiss if it isn't a focus event
    if (focusEvent.type !== "focus") {
      return false;
    }

    const target = ev.target as HTMLElement;

    const focusInRightClickMenu = !!(
      target
        ?.getAttribute("data-unique-id")
        ?.startsWith(UILocation.ContextMenu) ||
      target.id.startsWith(UILocation.ContextMenu)
    );
    this.setState({ focusInRightClickMenu });

    return focusInRightClickMenu;
  };

  // Return whether or not an item has a rightclick menu
  private supportsRightClickMenu = (): boolean => {
    return (
      Array.isArray(this.props.items) &&
      this.props.items?.some(
        (section: IContextualMenuItem) =>
          section.data?.contextMenuMap ||
          (Array.isArray(section.sectionProps?.items) &&
            section.sectionProps?.items?.some(
              (menuItem: IContextualMenuItem) =>
                menuItem.data?.contextMenuMap !== undefined
            ))
      )
    );
  };

  private getContextualMenuItemToExpand(
    items: IContextualMenuItem[]
  ): IContextualMenuItem | undefined {
    for (const item of items) {
      if (item.data?.shouldAutoOpen) {
        return item;
      }
      if (item.sectionProps?.items) {
        const subItem = this.getContextualMenuItemToExpand(
          item.sectionProps.items
        );
        if (subItem) {
          return subItem;
        }
      }
    }

    return undefined;
  }
}

export const AppContextualMenuCurrent = withMeasuredContext(
  withShadowRendering(AppContextualMenuBase)
);
